"use client";

import Link from 'next/link';
import Image from 'next/image';
import type { FC } from 'react';
import { useState, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { ShoppingCart, User, ChevronDown, Settings, ListOrdered, LogOut, Menu, X, ChevronRight, Receipt } from 'lucide-react';
import { signOut, useSession } from "next-auth/react";
import { useCart } from '@/app/contexts/cartContext';
import { formatServiceName } from '@/app/components/formatServiceName';

// --- Types ---
interface Training {
  id: number;
  trainingName: string;
  summary: string | null;
  price: number | null;
  imageUrl: string | null;
}

interface TrainingCategory {
  id: number;
  categoryName: string;
  Trainings: Training[];
}

interface Service {
  id: number;
  serviceName: string;
  summary: string | null;
  price: number | null;
  isQuantifiable: boolean | null;
}

interface ServiceCategory {
  id: number;
  categoryName: string;
  Services: Service[];
}

const HeaderAfter: FC = () => {
  const { data: session } = useSession();
  const [username, setUsername] = useState<string | null>(null);
  const [fullname, setFullname] = useState<string | null>(null);
  const { cartCount, openCart } = useCart();
  const pathname = usePathname();
  const router = useRouter();
  const [isMounted, setIsMounted] = useState(false);

  // Mobile & Account State
  const [isAccountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobileAccountOpen, setMobileAccountOpen] = useState(false);

  // Data State
  const [trainingCategories, setTrainingCategories] = useState<TrainingCategory[]>([]);
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([]);

  // Hover State
  const [isTrainingHovered, setIsTrainingHovered] = useState(false);
  const [isServiceHovered, setIsServiceHovered] = useState(false);

  // Active Category State
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null); // For Training
  const [activeServiceCategoryId, setActiveServiceCategoryId] = useState<number | null>(null); // For Services

  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const trainingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const serviceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- GENERIC HOVER HANDLER ---
  const handleMenuHover = (
    isEntering: boolean,
    setHoverState: React.Dispatch<React.SetStateAction<boolean>>,
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  ) => {
    if (isEntering) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setHoverState(true);
    } else {
      timeoutRef.current = setTimeout(() => {
        setHoverState(false);
      }, 200); // 200ms delay to prevent flickering
    }
  };

  // Fetch username
  useEffect(() => {
    const fetchUsername = async () => {
      if (session?.user?.email) {
        try {
          const response = await fetch('/api/auth/welcome-user', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: session.user.email }),
          });

          if (response.ok) {
            const data = await response.json();
            setUsername(data.username);
            setFullname(data.fullname);
          }
        } catch (error) {
          console.error("Error fetching username:", error);
        }
      }
    };
    fetchUsername();
  }, [session]);

  // Fetch Training Data
  useEffect(() => {
    const fetchTrainingData = async () => {
      try {
        const response = await fetch('/api/training');
        if (response.ok) {
          const data: TrainingCategory[] = await response.json();
          setTrainingCategories(data);
          if (data.length > 0) setActiveCategoryId(data[0].id);
        }
      } catch (error) {
        console.error("Failed to fetch training data", error);
      }
    };
    fetchTrainingData();
  }, []);

  // Fetch Service Data
  useEffect(() => {
    const fetchServiceData = async () => {
      try {
        const response = await fetch('/api/services?grouped=true');

        if (response.ok) {
          const data: ServiceCategory[] = await response.json();
          setServiceCategories(data);
          if (data.length > 0) setActiveServiceCategoryId(data[0].id);
        }
      } catch (error) {
        console.error("Failed to fetch service data", error);
      }
    };
    fetchServiceData();
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Handle outside click for account dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setAccountDropdownOpen(false);
      }
    };
    if (isAccountDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAccountDropdownOpen]);

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    await signOut({ redirect: false });
    router.replace('/login');
  };

  const toggleAccountDropdown = () => setAccountDropdownOpen(prev => !prev);
  const toggleMobileMenu = () => setMobileMenuOpen(prev => !prev);
  const toggleMobileAccount = () => setMobileAccountOpen(prev => !prev);
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileAccountOpen(false);
  };

  // Helper to get active items based on selected category
  const activeTrainings = trainingCategories.find(c => c.id === activeCategoryId)?.Trainings || [];
  const activeServices = serviceCategories.find(c => c.id === activeServiceCategoryId)?.Services || [];

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#1e2d4d] text-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-30">
          <div className="flex h-8 items-center justify-center text-xs md:text-sm md:justify-end">
            <div className="flex items-center gap-2">
              <span>{fullname ? `Welcome Back, ${fullname}!` : 'Welcome Back!'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 md:px-30 py-4 md:py-6 flex justify-between items-center relative">

          {/* Logo */}
          <div className="text-2xl font-bold text-gray-800 z-50">
            <Link
              href="/dashboard"
              onClick={(e) => {
                if (pathname === '/dashboard') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <Image
                src="/sdemarketplaceLogo2.png"
                alt="SDeMARKETPLACE Logo"
                width={180}
                height={40}
                className="h-12 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8 text-base h-full items-center">
            <Link href="/bundles" className={`hover:text-indigo-700 hover:font-medium ${pathname === '/bundles' ? 'text-indigo-700 font-medium' : 'text-gray-600'}`}>Bundles</Link>

            {/* Services Mega Menu */}
            <div
              className="h-full flex items-center"
              onMouseEnter={() => handleMenuHover(true, setIsServiceHovered, serviceTimeoutRef)}
              onMouseLeave={() => handleMenuHover(false, setIsServiceHovered, serviceTimeoutRef)}
            >
              <span
                className={`py-4 cursor-default transition-colors ${pathname === '/services' || pathname.startsWith('/services/') ? 'text-indigo-700 font-medium' : 'text-gray-600 hover:text-indigo-700 hover:font-medium'}`}
              >
                Services
              </span>

              {/* Mega Menu Dropdown */}
              {serviceCategories.length > 0 && (
                <div
                  className={`absolute top-full left-4 sm:left-6 md:left-30 right-4 sm:right-6 md:right-30 w-auto z-[60]
                              transition-all duration-300 ease-in-out origin-top
                              ${isServiceHovered ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}
                >
                  <div className="bg-white shadow-2xl rounded-b-xl border border-gray-100 overflow-hidden">
                    {/* Header Link */}
                    <div className="px-8 py-4 border-b border-gray-100 flex items-center bg-gray-50/50">
                      <Link
                        href="/services"
                        onClick={() => setIsServiceHovered(false)} // Close menu on click
                        className="text-indigo-600 text-lg font-bold hover:text-indigo-800 flex items-center gap-2 group/link"
                      >
                        View All Services <ChevronRight size={20} className="transition-transform group-hover/link:translate-x-1" />
                      </Link>
                    </div>

                    <div className="flex h-[470px]">
                      {/* Left Sidebar: Categories */}
                      <div className="w-1/3 bg-gray-50 overflow-y-auto py-2">
                        {serviceCategories.map((category) => (
                          <div
                            key={category.id}
                            onMouseEnter={() => setActiveServiceCategoryId(category.id)}
                            className={`cursor-pointer px-6 py-3 text-sm flex items-center justify-between transition-colors
                              ${activeServiceCategoryId === category.id
                                ? 'bg-white border-l-4 border-indigo-600 text-indigo-700 font-semibold shadow-sm'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 border-l-4 border-transparent'
                              }`}
                          >
                            {category.categoryName}
                            {activeServiceCategoryId === category.id && <ChevronRight size={14} />}
                          </div>
                        ))}
                      </div>

                      {/* Right Content: Service Cards */}
                      <div className="w-2/3 p-6 overflow-y-auto bg-white">
                        {activeServices.length > 0 ? (
                          <>
                            {/* 1. Filter the services */}
                            {(() => {
                              const instantServices = activeServices.filter((s) => s.isQuantifiable === true);
                              const subServices = activeServices.filter((s) => !s.isQuantifiable);

                              return (
                                <>
                                  {/* Section 1: Instant Purchase */}
                                  {instantServices.length > 0 && (
                                    <div className="mb-8">
                                      <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
                                        Instant Purchase
                                      </h3>
                                      <div className="grid grid-cols-3 gap-4">
                                        {instantServices.map((service) => (
                                          <Link
                                            key={service.id}
                                            href={`/services/${service.id}`}
                                            onClick={() => setIsServiceHovered(false)}
                                            className="group/card block h-full p-4 rounded-lg border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all duration-200 bg-white flex flex-col"
                                          >
                                            <div className="flex items-start gap-3 flex-1">
                                              <div>
                                                <h4 className="text-sm font-bold text-gray-800 group-hover/card:text-indigo-700 mb-1">
                                                  {formatServiceName(service.serviceName)}
                                                </h4>
                                                <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
                                                  {service.summary || "No summary available."}
                                                </p>
                                              </div>
                                            </div>
                                          </Link>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Section 2: Subscription-based Purchase */}
                                  {subServices.length > 0 && (
                                    <div className="mb-2">
                                      <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
                                        Subscription-based Purchase
                                      </h3>
                                      <div className="grid grid-cols-3 gap-4">
                                        {subServices.map((service) => (
                                          <Link
                                            key={service.id}
                                            href={`/services/${service.id}`}
                                            onClick={() => setIsServiceHovered(false)}
                                            className="group/card block h-full p-4 rounded-lg border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all duration-200 bg-white flex flex-col"
                                          >
                                            <div className="flex items-start gap-3 flex-1">
                                              <div>
                                                <h4 className="text-sm font-bold text-gray-800 group-hover/card:text-indigo-700 mb-1">
                                                  {formatServiceName(service.serviceName)}
                                                </h4>
                                                <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
                                                  {service.summary || "No summary available."}
                                                </p>
                                              </div>
                                            </div>
                                          </Link>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm">
                            <p>No services available in this category.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Training Mega Menu */}
            <div
              className="h-full flex items-center"
              onMouseEnter={() => handleMenuHover(true, setIsTrainingHovered, trainingTimeoutRef)}
              onMouseLeave={() => handleMenuHover(false, setIsTrainingHovered, trainingTimeoutRef)}
            >
              <span
                className={`py-4 cursor-default transition-colors ${pathname === '/training' || pathname.startsWith('/training/') ? 'text-indigo-700 font-medium' : 'text-gray-600 hover:text-indigo-700 hover:font-medium'}`}
              >
                Training
              </span>

              {/* Mega Menu Dropdown */}
              {trainingCategories.length > 0 && (
                <div
                  className={`absolute top-full left-4 sm:left-6 md:left-30 right-4 sm:right-6 md:right-30 w-auto z-[60]
                              transition-all duration-300 ease-in-out origin-top
                              ${isTrainingHovered ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}
                >
                  <div className="bg-white shadow-2xl rounded-b-xl border border-gray-100 overflow-hidden">
                    {/* Header Link */}
                    <div className="px-8 py-4 border-b border-gray-100 flex items-center bg-gray-50/50">
                      <Link
                        href="/training"
                        onClick={() => setIsTrainingHovered(false)} // Close menu on click
                        className="text-indigo-600 text-lg font-bold hover:text-indigo-800 flex items-center gap-2 group/link"
                      >
                        View All Trainings <ChevronRight size={20} className="transition-transform group-hover/link:translate-x-1" />
                      </Link>
                    </div>

                    <div className="flex h-[470px]">
                      {/* Left Sidebar: Categories */}
                      <div className="w-1/3 bg-gray-50 overflow-y-auto py-2">
                        {trainingCategories.map((category) => (
                          <div
                            key={category.id}
                            onMouseEnter={() => setActiveCategoryId(category.id)}
                            className={`cursor-pointer px-6 py-3 text-sm flex items-center justify-between transition-colors
                              ${activeCategoryId === category.id
                                ? 'bg-white border-l-4 border-indigo-600 text-indigo-700 font-semibold shadow-sm'
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 border-l-4 border-transparent'
                              }`}
                          >
                            {category.categoryName}
                            {activeCategoryId === category.id && <ChevronRight size={14} />}
                          </div>
                        ))}
                      </div>

                      {/* Right Content: Training Cards */}
                      <div className="w-2/3 p-6 overflow-y-auto bg-white">
                        {activeTrainings.length > 0 ? (
                          <div className="grid grid-cols-3 gap-4">
                            {activeTrainings.map((training) => (
                              <Link
                                key={training.id}
                                href={`/training/${training.id}`}
                                onClick={() => setIsTrainingHovered(false)} // Close menu on click
                                className="group/card block h-full p-4 rounded-lg border border-gray-100 hover:border-indigo-200 hover:shadow-md transition-all duration-200 bg-white flex flex-col"
                              >
                                <div className="flex items-start gap-3 flex-1">
                                  <div>
                                    <h4 className="text-sm font-bold text-gray-800 group-hover/card:text-indigo-700 mb-1">
                                      {training.trainingName}
                                    </h4>
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                      {training.summary || "No summary available."}
                                    </p>
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </div>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm">
                            <p>No trainings available in this category.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Link href="/support" className={`hover:text-indigo-700 hover:font-medium ${pathname === '/support' ? 'text-indigo-700 font-medium' : 'text-gray-600'}`}>Support</Link>
            <Link href="/aboutus" className={`hover:text-indigo-700 hover:font-medium ${pathname === '/aboutus' ? 'text-indigo-700 font-medium' : 'text-gray-600'}`}>About Us</Link>
          </nav>

          {/* Desktop Action Icons */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={openCart}
              className="relative text-gray-600 hover:text-indigo-700"
              aria-label="Open cart"
              suppressHydrationWarning>
              <ShoppingCart size={23} />
              {isMounted && (
                <span className="absolute -top-1 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-xs text-white">
                  {cartCount}
                </span>
              )}
            </button>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleAccountDropdown}
                className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-md hover:bg-indigo-700 text-base transition-colors"
                suppressHydrationWarning>
                <User size={19} />
                <span>Account</span>
                <ChevronDown size={19} className={`transform transition-transform duration-300 ${isAccountDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isAccountDropdownOpen && (
                <div className="absolute right-0 mt-4 w-64 bg-white rounded-lg shadow-xl overflow-hidden z-50 border border-gray-200">
                  <div className="px-5 pt-5 pb-3">
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex items-center justify-center h-16 w-16 rounded-full bg-indigo-600 text-white font-bold text-2xl">
                        {username?.match(/[a-zA-Z]/)?.[0]?.toUpperCase() || <User size={24} />}
                      </div>
                      <div className="text-center">
                        <p className="font-semibold text-gray-800 text-sm">{username || 'User'}</p>
                        <p className="text-xs text-gray-500">{session?.user?.email}</p>
                      </div>
                    </div>
                  </div>
                  <div className="px-5"><hr className="border-gray-200" /></div>
                  <nav className="py-2">
                    <Link href="/account-settings" className="block" onClick={() => setAccountDropdownOpen(false)}>
                      <div className="flex items-center gap-3 mx-3 my-1 rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-indigo-700">
                        <Settings size={20} /><span>Account Settings</span>
                      </div>
                    </Link>
                    <Link href="/order-history" className="block" onClick={() => setAccountDropdownOpen(false)}>
                      <div className="flex items-center gap-3 mx-3 my-1 rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-indigo-700">
                        <ListOrdered size={20} /><span>Order History</span>
                      </div>
                    </Link>
                    {/* <Link href="/payment-history" className="block" onClick={() => setAccountDropdownOpen(false)}>
                      <div className="flex items-center gap-3 mx-3 my-1 rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-indigo-700">
                        <Receipt size={20} /><span>Payment History</span>
                      </div>
                    </Link> */}
                    <div className="px-5 py-2"><hr className="border-gray-200" /></div>
                    <button onClick={handleLogout} className="w-full text-left">
                      <div className="flex items-center gap-3 mx-3 my-1 rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-indigo-700">
                        <LogOut size={20} /><span>Logout</span>
                      </div>
                    </button>
                  </nav>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-4">
            <button
              onClick={openCart}
              className="relative text-gray-600 hover:text-indigo-700"
              aria-label="Open cart"
              suppressHydrationWarning>
              <ShoppingCart size={22} />
              {isMounted && (
                <span className="absolute -top-1 -right-2 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-xs text-white">
                  {cartCount}
                </span>
              )}
            </button>
            <button onClick={toggleMobileMenu} className="text-gray-600 hover:text-indigo-700">
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg absolute top-full left-0 w-full z-40">
            <nav className="flex flex-col items-start space-y-5 py-5 px-8">
              <Link href="/bundles" className={`text-sm hover:text-indigo-700 hover:font-medium ${pathname === '/bundles' ? 'text-indigo-700 font-medium' : 'text-gray-600'}`} onClick={closeMobileMenu}>Bundles</Link>
              <Link href="/services" className={`text-sm hover:text-indigo-700 hover:font-medium ${pathname === '/services' ? 'text-indigo-700 font-medium' : 'text-gray-600'}`} onClick={closeMobileMenu}>Services</Link>
              <Link href="/training" className={`text-sm hover:text-indigo-700 hover:font-medium ${pathname === '/training' ? 'text-indigo-700 font-medium' : 'text-gray-600'}`} onClick={closeMobileMenu}>Training</Link>
              <Link href="/support" className={`text-sm hover:text-indigo-700 hover:font-medium ${pathname === '/support' ? 'text-indigo-700 font-medium' : 'text-gray-600'}`} onClick={closeMobileMenu}>Support</Link>
              <Link href="/aboutus" className={`text-sm hover:text-indigo-700 hover:font-medium ${pathname === '/aboutus' ? 'text-indigo-700 font-medium' : 'text-gray-600'}`} onClick={closeMobileMenu}>About Us</Link>

              {/* Collapsible Account Section */}
              <div className="w-full flex flex-col items-start">
                <button
                  onClick={toggleMobileAccount}
                  className={`w-full flex items-center justify-between text-sm hover:text-indigo-700 hover:font-medium ${isMobileAccountOpen ? 'text-indigo-700 font-medium' : 'text-gray-600'}`}
                >
                  <span>Account</span>
                  <ChevronDown
                    size={15}
                    className={`transform transition-transform duration-300 ${isMobileAccountOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isMobileAccountOpen && (
                  <div className="w-full flex flex-col items-start space-y-5 mt-5 pl-3">
                    <Link href="/account-settings" className="text-sm flex items-center gap-3 text-gray-500 hover:text-indigo-700 hover:font-medium" onClick={closeMobileMenu}>
                      <span>Account Settings</span>
                    </Link>
                    <Link href="/order-history" className="text-sm flex items-center gap-3 text-gray-500 hover:text-indigo-700 hover:font-medium" onClick={closeMobileMenu}>
                      <span>Order History</span>
                    </Link>
                    {/* <Link href="/payment-history" className="text-sm flex items-center gap-3 text-gray-500 hover:text-indigo-700 hover:font-medium" onClick={closeMobileMenu}>
                      <span>Payment History</span>
                    </Link> */}
                  </div>
                )}
              </div>

              <div className="w-full border-t border-gray-200"></div>

              <div className="w-full text-center">
                <p className="text-xs text-gray-500">{session?.user?.email}</p>
              </div>

              <div className="w-full flex justify-center">
                <button
                  onClick={handleLogout}
                  className="bg-indigo-600 text-white w-full text-center px-6 py-2.5 rounded-md hover:bg-indigo-700 text-sm"
                >
                  Logout
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default HeaderAfter;